/**
 * Brand cache — Sub-agent KKKKK Track 1 (2026-07-29).
 *
 * Small localStorage helper for the workspace brand theme (primary +
 * accent colors, logo r2 key + updated_at, account_id). Shared between
 * useBrandTheme.tsx (writer + reader on module-init) and useSession.tsx
 * (clearer on sign-out). Extracted into its own module to avoid a
 * circular import between the two hooks.
 *
 * Closes the paint gap between SPA mount + /api/me completion: on next
 * mount, useBrandTheme reads the cache synchronously (before React
 * mounts) and injects the brand style tag + favicon override so first
 * paint carries the last-known workspace brand instead of the Olytic
 * default. Once /api/me resolves, BrandThemeProvider refreshes the
 * cache with the fresh values.
 *
 * Sign-out clears the cache so a subsequent user on the same browser
 * (potentially a different workspace) doesn't inherit the previous
 * tenant's brand.
 */

const BRAND_CACHE_KEY = 'olytic:brand-cache-v1';

export interface BrandCache {
  account_id: string | null;
  brand_primary_color: string | null;
  brand_accent_color: string | null;
  brand_logo_r2_key: string | null;
  brand_logo_updated_at: string | null;
}

export function readBrandCache(): BrandCache | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(BRAND_CACHE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const obj = parsed as Record<string, unknown>;
    return {
      account_id: typeof obj.account_id === 'string' ? obj.account_id : null,
      brand_primary_color:
        typeof obj.brand_primary_color === 'string' ? obj.brand_primary_color : null,
      brand_accent_color:
        typeof obj.brand_accent_color === 'string' ? obj.brand_accent_color : null,
      brand_logo_r2_key:
        typeof obj.brand_logo_r2_key === 'string' ? obj.brand_logo_r2_key : null,
      brand_logo_updated_at:
        typeof obj.brand_logo_updated_at === 'string' ? obj.brand_logo_updated_at : null,
    };
  } catch {
    return null;
  }
}

export function writeBrandCache(cache: BrandCache): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* best-effort */
  }
}

export function clearBrandCache(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(BRAND_CACHE_KEY);
  } catch {
    /* best-effort */
  }
}
